import React, { useState, useMemo, useEffect, useRef } from "react";
import * as R from "ramda";
import { compose } from "recompose";
import { connect } from "react-redux";
import { Popconfirm, Timeline, Icon, Row, Col } from "antd";
import { Content } from "@worcket/common/components/layout";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import {
  unpublishOpportunity,
  UNPUBLISH_OPPORTUNITY,
  closeOpportunity,
  CLOSE_OPPORTUNITY
} from "../../../../../store/actions/opportunity";
import { isLoading } from "../../../../../utils/actions";
import Button from "../../../../../components/Button";

import CloseOpportunityModal from "./CloseOpportunityModal";

import styled, { withTheme } from "styled-components/macro";

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-top: 1px solid #f0f2f5;
  margin-top: 0;
  padding: 20px 0;
  margin-top: 20px;
`;

const StyledTimeline = styled(Timeline)`
  margin-top: 32px !important;
  margin-left: 8px !important;

  .anticon {
    font-size: 24px;
  }

  .generateButton .anticon {
    font-size: 14px;
  }

  .ant-timeline-item {
    margin-bottom: 12px;
  }

  .ant-timeline-item:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .ant-timeline-item-content {
    margin-left: 32px !important;
    display: flex;
    flex-direction: column;
  }

  .subtitle {
    margin-top: 2px;
  }

  .title {
    font-weight: 600;
  }
`;

const PublishingForm = ({
  theme,
  saving,
  isDisabled,
  handleClose,
  loadingClose,
  handleUnpublish,
  loadingUnpublish,
  opportunity,
  section
}) => {
  const { t } = useTranslation(["opportunity", "form"]);
  const sectionName = useMemo(() => t("opportunity:publishing.sectionName"), [
    t
  ]);
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);
  const [closeModalVisible, setCloseModalVisible] = useState(false);

  const {
    id,
    open,
    status,
    createdAt,
    published,
    closingReason,
    closingReasonOther
  } = opportunity;

  const statusText = useMemo(
    () => ({
      open: t("opportunity:publishing.openText"),
      published: t("opportunity:publishing.publishedText"),
      finished: t("opportunity:publishing.finishedText")
    }),
    [t]
  );

  const closeOpportunitySubmit = ({
    closeMotive,
    closeReason,
    otherReason
  }) => {
    const closingReason =
      closeMotive !== "position-filled" ? closeReason : closeMotive;
    const closingReasonOther = closingReason === "other" && otherReason;
    setCloseModalVisible(false);
    handleUnpublish({
      id,
      closingReason,
      closingReasonOther
    });
  };

  const createdAndPublished = createdAt === published;

  const showTimeline = R.both(
    R.either(R.has("open"), R.has("published")),
    R.has("createdAt")
  )(opportunity);

  const formatDate = timestamp => moment(timestamp).format("LLL");

  return (
    <div ref={ref} data-test="publishing-form">
      <Content>
        <h2>{t("opportunity:publishing.title")}</h2>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            {showTimeline && (
              <StyledTimeline data-test="timeline">
                <Timeline.Item
                  data-test={
                    createdAndPublished
                      ? "timeline-item-created-published"
                      : "timeline-item-created-draft"
                  }
                  dot={
                    createdAndPublished ? (
                      <Icon
                        type="notification"
                        theme="twoTone"
                        twoToneColor={theme["@primary"]}
                      />
                    ) : (
                      <Icon
                        type="file-text"
                        theme="outlined"
                        style={{ color: theme["@grey-darker"] }}
                      />
                    )
                  }
                >
                  <span className="title">
                    {t(
                      `opportunity:publishing.${
                        createdAndPublished
                          ? "createdAndPublished"
                          : "createdAsDraft"
                      }`
                    )}
                  </span>
                  <span className="subtitle">{formatDate(createdAt)}</span>
                </Timeline.Item>
                {published && !(createdAt === published) && (
                  <Timeline.Item
                    data-test="timeline-item-published"
                    dot={
                      <Icon
                        type="notification"
                        theme="twoTone"
                        twoToneColor={theme["@primary"]}
                      />
                    }
                  >
                    <span className="title">
                      {t("opportunity:publishing.published")}
                    </span>
                    <span className="subtitle">{formatDate(published)}</span>
                  </Timeline.Item>
                )}
                {status === "finished" && open && (
                  <Timeline.Item
                    data-test="timeline-item-closed"
                    dot={
                      <Icon
                        type="close-circle"
                        theme="twoTone"
                        twoToneColor={theme["@error"]}
                      />
                    }
                  >
                    <span className="title">
                      {t("opportunity:publishing.closed")}
                    </span>
                    {closingReason && (
                      <span className="subtitle">
                        {t("opportunity:publishing.reason")}
                        {": "}
                        {closingReason === "other"
                          ? closingReasonOther
                          : t(
                              `opportunity:publishing.closeOpportunityForm.${closingReason}`
                            )}
                      </span>
                    )}
                    <span className="subtitle">{formatDate(open)}</span>
                  </Timeline.Item>
                )}
              </StyledTimeline>
            )}
            {!showTimeline && (
              <p data-test="opportunity-status">{statusText[status]}</p>
            )}
          </Col>
        </Row>
        {status !== "finished" && (
          <ActionsContainer>
            {(status === "open" || !status) && (
              <>
                <Popconfirm
                  title={t("opportunity:publishing.deletePrompt")}
                  onConfirm={() => handleClose(id)}
                  okText={t("opportunity:publishing.deleteConfirm")}
                  cancelText={t("global:no")}
                >
                  <Button
                    data-test="delete-btn"
                    type="alert"
                    size="lg"
                    ghost
                    disabled={saving || isDisabled || status !== "open"}
                    loading={loadingClose}
                  >
                    {t("opportunity:publishing.delete")}
                  </Button>
                </Popconfirm>
              </>
            )}

            {status === "published" && (
              <>
                <Button
                  data-test="close-opportunity-btn"
                  size="lg"
                  type="alert"
                  onClick={() => setCloseModalVisible(true)}
                  disabled={saving || isDisabled}
                  loading={loadingUnpublish}
                  style={{ margin: "0 auto" }}
                >
                  {t("opportunity:publishing.close")}
                </Button>
                {closeModalVisible && (
                  <CloseOpportunityModal
                    t={t}
                    onSubmit={closeOpportunitySubmit}
                    onClose={() => setCloseModalVisible(false)}
                  />
                )}
              </>
            )}
          </ActionsContainer>
        )}
      </Content>
    </div>
  );
};

export default compose(
  connect(
    state => ({
      saving: R.anyPass([
        state => isLoading(UNPUBLISH_OPPORTUNITY, state),
        state => isLoading(CLOSE_OPPORTUNITY, state)
      ])(state),
      loadingClose: isLoading(CLOSE_OPPORTUNITY, state),
      loadingUnpublish: isLoading(UNPUBLISH_OPPORTUNITY, state)
    }),
    {
      handleUnpublish: unpublishOpportunity,
      handleClose: closeOpportunity
    }
  ),
  withTheme
)(PublishingForm);
